import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { taxAPI, formatCurrency, formatPercent } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const OvertimeCalculator = () => {
  const [filing, setFiling] = useState("single");
  const [overtime, setOvertime] = useState("8000");
  const [magi, setMagi] = useState("65000");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.overtime({
        annual_overtime: Number(overtime),
        filing_status: filing,
        magi: Number(magi),
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="ot-filing" />
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Annual Overtime Premium Pay</label>
        <input
          type="number"
          value={overtime}
          onChange={(e) => setOvertime(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-overtime"
        />
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
          The premium portion only — the half-time above regular rate
        </p>
      </div>
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Modified AGI (MAGI)</label>
        <input
          type="number"
          value={magi}
          onChange={(e) => setMagi(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-magi"
        />
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate Overtime Deduction"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="ot-result">
      <div className="border-2 border-foreground bg-foreground text-background p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">Your OBBBA Overtime Deduction</span>
        <h3 className="font-heading text-3xl md:text-4xl font-bold leading-tight mb-2">
          You can deduct <span className="text-primary">{formatCurrency(result.eligible_deduction)}</span> of overtime pay.
        </h3>
        <p className="font-body text-sm text-background/80 mt-3">
          Estimated federal tax savings: <span className="font-mono font-semibold">{formatCurrency(result.estimated_federal_tax_savings)}</span> at your {formatPercent(result.marginal_rate_used, 0)} marginal rate.
        </p>
      </div>
      <div className="bg-card border-2 border-foreground overflow-hidden">
        <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
          <span className="font-body text-sm">Statutory Max</span>
          <span className="font-mono tabular-nums text-right">{formatCurrency(result.max_deduction)}</span>
        </div>
        <div className="p-5 border-b border-foreground/15 grid grid-cols-2 gap-4">
          <span className="font-body text-sm">Phase-Out Starts</span>
          <span className="font-mono tabular-nums text-right">{formatCurrency(result.phaseout_threshold)} MAGI</span>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <span className="font-body text-sm">Phase-Out Applied</span>
          <span className={`font-mono text-right font-semibold ${result.phaseout_applied ? "text-destructive" : "text-primary"}`}>
            {result.phaseout_applied ? "Yes" : "No"}
          </span>
        </div>
      </div>
      <div className="bg-muted border border-foreground/15 p-5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Note</span>
        <p className="font-body text-sm">{result.note}</p>
      </div>
      <EmailGatePdfButton
        testid="ot-pdf"
        filename="taxshift-2026-overtime.pdf"
        pdfPayload={{
          title: "OBBBA No Tax on Overtime Analysis",
          subtitle: `${result.inputs.filing_status.replace("_", " ").toUpperCase()} filer`,
          inputs: [
            { label: "Overtime Premium", value: formatCurrency(result.inputs.annual_overtime) },
            { label: "MAGI", value: formatCurrency(result.inputs.magi) },
            { label: "Filing Status", value: result.inputs.filing_status.replace("_", " ").toUpperCase() },
          ],
          headline: `You can deduct ${formatCurrency(result.eligible_deduction)} of overtime — estimated federal savings ${formatCurrency(result.estimated_federal_tax_savings)}.`,
          rows: [
            { label: "Max Deduction", v2025: formatCurrency(result.max_deduction), v2026: formatCurrency(result.max_deduction) },
            { label: "Phase-Out Threshold", v2025: formatCurrency(result.phaseout_threshold), v2026: formatCurrency(result.phaseout_threshold) },
            { label: "Eligible Deduction", v2025: formatCurrency(result.eligible_deduction), v2026: formatCurrency(result.eligible_deduction) },
            { label: "Estimated Tax Savings", v2025: formatCurrency(result.estimated_federal_tax_savings), v2026: formatCurrency(result.estimated_federal_tax_savings) },
          ],
        }}
      />
      <ResultExplainer slug="no-tax-on-overtime" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="ot-empty">
      <p className="font-heading text-xl">Enter your overtime pay and click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="No Tax on Overtime Calculator (OBBBA 2026)"
      slug="no-tax-on-overtime"
      overline="Calculator · OBBBA · Overtime"
      description="OBBBA created a new deduction for overtime premium pay of up to $12,500 (single) or $25,000 (married joint), for tax years 2025-2028. Eligible non-exempt FLSA workers can deduct the premium half of overtime hours."
      seoTitle="No Tax on Overtime Calculator 2026 — OBBBA Deduction"
      seoKeywords="no tax on overtime calculator, overtime deduction 2026, obbba overtime, flsa overtime tax, time and a half"
      jsonLd={buildCalculatorSchema({
        name: "No Tax on Overtime Calculator (OBBBA)",
        description: "Free calculator for OBBBA's overtime premium pay deduction.",
        url: "/calculator/no-tax-on-overtime",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">How the OBBBA overtime deduction works</h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              OBBBA introduced an above-the-line deduction of up to <strong>$12,500</strong> (single filers) or <strong>$25,000</strong>
              (married filing jointly) for overtime premium pay received by non-exempt employees subject to FLSA overtime rules.
              The deduction is available for tax years 2025-2028, regardless of whether you itemize.
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">What counts as "overtime premium"</h3>
            <p>
              Only the <em>premium</em> portion — the extra half-time pay above your regular rate — qualifies. If you work 10 overtime hours at
              $20/hour regular pay and $30/hour overtime pay, the premium is $10 × 10 = $100 (not the full $300 overtime).
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Who qualifies</h3>
            <p>
              Only non-exempt employees subject to FLSA overtime requirements. Salaried/exempt employees do not qualify.
              Self-employed individuals do not qualify for this specific deduction.
            </p>
            <h3 className="font-heading text-xl font-semibold mt-6">Phase-out</h3>
            <p>
              Deduction reduces by $100 per $1,000 of MAGI above $150,000 (single) / $300,000 (married joint).
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "no-tax-on-tips", label: "No Tax on Tips" },
        { slug: "tax-bracket-2026", label: "Tax Bracket" },
      ]}
    />
  );
};

export default OvertimeCalculator;
